import items from "./items.json";
import { money } from "../utils/format";
import { validateInventory } from "../utils/inventory";
import { liveInventoryConfigured, readLiveInventory } from "../utils/liveInventory";

export let ITEMS = items;
export function replaceInventory(input) { ITEMS = validateInventory(input); }
export async function loadPublishedInventory() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    if (process.env.REACT_APP_INVENTORY_SOURCE === "facebook") {
      clearTimeout(timeout);
      const liveTimeout = setTimeout(() => controller.abort(), 25000);
      try {
        const response = await fetch("/api/marketplace", { cache: "no-store", signal: controller.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error?.message || "Facebook listings are unavailable.");
        if (data.source !== "facebook" || data.sellerId !== "61567154155885" || !Array.isArray(data.items)) throw new Error("Invalid Facebook listings response.");
        ITEMS = data.items;
        return "";
      } finally { clearTimeout(liveTimeout); }
    }
    if (liveInventoryConfigured) {
      const snapshot = await readLiveInventory(controller.signal);
      replaceInventory(snapshot.items);
      return "";
    }
    const response = await fetch(`${process.env.PUBLIC_URL}/inventory.json`, { cache: "no-store", signal: controller.signal });
    if (!response.ok) throw new Error(`Inventory request returned ${response.status}.`);
    replaceInventory(await response.json());
    return "";
  } catch (error) {
    if (process.env.REACT_APP_INVENTORY_SOURCE === "facebook") {
      ITEMS = [];
      return "We couldn’t load the latest Facebook listings. Please try again shortly or view our Facebook Marketplace profile.";
    }
    console.warn("Using bundled inventory:", error.message);
    return `Published inventory could not be loaded. The website is using its bundled listings. ${error.message}`;
  } finally { clearTimeout(timeout); }
}

export const CATEGORY_ORDER = [
  "All",
  "Beauty & Personal Care",
  "Apparel",
  "Shoes",
  "Toys",
  "Household",
  "Seasonal & Miscellaneous",
];

export const SUBCATEGORIES = {
  "Beauty & Personal Care": ["Makeup", "Hair", "Hand & Nails", "Face", "Body", "Oral", "Tools", "Fragrance", "Personal Hygiene"],
  Apparel: ["Men", "Women", "Kid", "Accessories"],
  Shoes: ["Men", "Women", "Kid", "Baby"],
  Toys: ["Dolls", "Play-Doh", "Figure Set", "LEGO & Building Sets", "Arts", "Vehicles & More", "Blasters", "Ride-Ons", "Pretend Toys", "Plush", "Games"],
  Household: ["Household Supplies", "Home & Kitchen", "Pet", "Health & Wellness", "Baby", "Appliance"],
  "Seasonal & Miscellaneous": ["Christmas", "Halloween", "Outdoor"],
};

const SHOE_SIZES = {
  Men: ["6", "7", "8", "9", "10", "11", "12"],
  Women: ["6", "7", "8", "9", "10", "11", "12", "13"],
  Kid: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13"],
};

const APPAREL_SIZES = {
  Men: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
  Women: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
  Kid: ["2T", "3T", "4T", "5T", "XS (4-5)", "S (5-6)", "M (7-8)", "L (10-12)", "XL (12-14)"],
};

export const SORT_OPTIONS = [
  { value: "new", label: "Sort: Latest" },
  { value: "low", label: "Sort: Price Low to High" },
  { value: "high", label: "Sort: Price High to Low" },
];

const LATEST_LIMIT = 24;

export function getItem(id) {
  return ITEMS.find((x) => String(x.id) === String(id));
}

export function normalizedCategory(x) {
  const c = x.displayCategory || "";
  if (c === "Toys & Kids") return "Toys";
  if (c === "Home") return "Household";
  if (CATEGORY_ORDER.includes(c)) return c;
  return c === "Beauty" ? "Beauty & Personal Care" : c;
}

export function inferSubcategory(x) {
  if (x.subcategory) {
    const department = normalizedCategory(x);
    if (department === "Apparel" && ["Boy", "Girl", "Toddler", "Unisex Kids", "Kids"].includes(x.subcategory)) return "Kid";
    if (department === "Shoes" && x.subcategory === "Kids") return "Kid";
    return x.subcategory;
  }
  const n = `${x.name} ${x.category || ""}`.toLowerCase();
  const c = normalizedCategory(x);
  if (c === "Beauty & Personal Care") {
    if (/tampon|sanitary|liner|pads with wings/.test(n)) return "Personal Hygiene";
    if (/perfume|fragrance|cologne|eau de|body mist/.test(n)) return "Fragrance";
    if (/tooth|denture|interdental|oral|mouth/.test(n)) return "Oral";
    if (/shampoo|conditioner|hair|edge tamer|nexxus|marc anthony/.test(n)) return "Hair";
    if (/nail|polish|manicure|pedicure/.test(n)) return "Hand & Nails";
    if (/brush|sponge|tweezer|curler|beauty tool/.test(n)) return "Tools";
    if (/mascara|lip|makeup|foundation|concealer|eyeliner|shadow|blush/.test(n)) return "Makeup";
    if (/face|facial|cleanser|micell|moistur|serum|scrub|mask|toner|skin rescue|hydractiv|collagen bank|lip balm|lip plump|neck|jawline/.test(n)) return "Face";
    return "Body";
  }
  if (c === "Apparel") {
    if (/toddler|kid|child|youth|girl|boy/.test(n)) return "Kid";
    if (/women|ladies|woman/.test(n)) return "Women";
    if (/men|mens|man's|male/.test(n)) return "Men";
    if (/girl/.test(n)) return "Girl";
    if (/boy/.test(n)) return "Boy";
    return "Women";
  }
  if (c === "Shoes") {
    if (/baby|infant/.test(n)) return "Baby";
    if (/kid|child|girl|boy|youth/.test(n)) return "Kid";
    if (/women|ladies|woman/.test(n)) return "Women";
    return "Men";
  }
  if (c === "Toys") {
    if (/nerf|xshot|blaster/.test(n)) return "Blasters";
    if (/ride-on|ride on|whisper ride/.test(n)) return "Ride-Ons";
    if (/lego|mega pok[eé]mon|tiletown|playmobil/.test(n)) return "LEGO & Building Sets";
    if (/baby alive|barbie|royal high/.test(n)) return "Dolls";
    if (/play-doh|playdoh|crayola|bracelet|lip gloss/.test(n)) return "Arts";
    if (/thready bear|squishmallow|plush/.test(n)) return "Plush";
    if (/trainer guess|money safe|saving bank|smashers|knuckle crunchers|sega genesis/.test(n)) return "Games";
    if (/kitchen|oven|shopping basket|candy cart|clinic|casita|aquarium|bathroom|dollhouse|doll house|creative chefs|dohkins|miniverse|cookeez|casdon|cakey/.test(n)) return "Pretend Toys";
    return "Vehicles & More";
  }
  if (c === "Household") {
    if (/pet|dog|cat/.test(n)) return "Pet";
    if (/baby|diaper|infant/.test(n)) return "Baby";
    if (/health|wellness|vitamin|medical/.test(n)) return "Health & Wellness";
    if (/outdoor|garden|patio|camp/.test(n)) return "Outdoor";
    if (/appliance|vacuum|blender|toaster|kettle|microwave/.test(n)) return "Appliance";
    if (/kitchen|desk|home|cook|pan|pot/.test(n)) return "Home & Kitchen";
    return "Household Supplies";
  }
  if (c === "Seasonal & Miscellaneous") {
    if (/christmas|holiday|fir|pine tree/.test(n)) return "Christmas";
    if (/halloween/.test(n)) return "Halloween";
    return "Outdoor";
  }
  return "";
}

export function sizesFor(department, subcategory) {
  if (department === "Apparel") return APPAREL_SIZES[subcategory] || null;
  if (department === "Shoes") return SHOE_SIZES[subcategory] || null;
  return null;
}

function matchesSize(x, size) {
  if (size === "All") return true;
  return (x.selections || []).some((v) => String(v).toUpperCase() === String(size).toUpperCase());
}

function matchesSubcategory(x, activeSub) {
  if (activeSub === "All") return true;
  if (Array.isArray(x.subcategories) && x.subcategories.includes(activeSub)) return true;
  const sub = inferSubcategory(x);
  const category = normalizedCategory(x);
  if (category === "Apparel") {
    if (activeSub === "Kid") return ["Kid", "Boy", "Girl", "Toddler", "Unisex Kids"].includes(sub);
    if (activeSub === "Accessories") return sub === "Accessories";
    if (sub === "Unisex Adult") return activeSub === "Men" || activeSub === "Women";
  }
  if (category === "Shoes" && activeSub === "Kid") return sub === "Kid" || sub === "Kids";
  return sub === activeSub;
}

export function normalizeSearchText(v) {
  return String(v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function sortItems(list, sort) {
  const sorted = list.slice();
  if (sort === "low") sorted.sort((a, b) => a.price - b.price || a.id - b.id);
  else if (sort === "high") sorted.sort((a, b) => b.price - a.price || a.id - b.id);
  else sorted.sort((a, b) => b.id - a.id);
  return sorted;
}

export function searchItems(query) {
  const q = normalizeSearchText(query);
  if (!q) return [];
  return sortItems(
    ITEMS.filter((x) => {
      const haystack = normalizeSearchText(
        [x.name, normalizedCategory(x), inferSubcategory(x), x.size, x.deal, (x.selections || []).join(" ")].join(" "),
      );
      return haystack.includes(q);
    }),
    "new",
  );
}

export function filterItems({ department = "All", subcategory = "All", size = "All", sort = "new", latest = false }) {
  if (latest) return sortItems(ITEMS, "new").slice(0, LATEST_LIMIT);
  return sortItems(
    ITEMS.filter(
      (x) =>
        (department === "All" || normalizedCategory(x) === department) &&
        matchesSubcategory(x, subcategory) &&
        matchesSize(x, size),
    ),
    sort,
  );
}

export function displayPriceText(x, includeSize = true) {
  let text = x.priceUnset ? "Price not provided" : money(x.price);
  const name = String(x.name || "").toLowerCase();
  if (!x.priceUnset) {
    if (x.saleUnit === "set") text += " per set";
    else if (name.includes("kotex")) text += " per box";
    else if (name.includes("cascade")) text += " per container";
    else if (!["Apparel", "Shoes", "Toys", "Household", "Seasonal & Miscellaneous"].includes(normalizedCategory(x))) text += " each";
  }
  if (x.deal) text += ` · ${x.deal}`;
  if (includeSize && x.size) text += ` · ${x.size}`;
  return text;
}

// Selectable options: blank values and the placeholder "Clear" are never offered.
export function usableChoices(x) {
  return (x.selections || []).filter((v) => String(v || "").trim() && !/^clear$/i.test(String(v)));
}

export function isChoiceAvailable(x, choice) {
  return stockMax(x) > 0 && !(x.unavailableSelections || []).some((v) => String(v).trim().toLowerCase() === String(choice).trim().toLowerCase());
}

export function stockMax(x) {
  if (x?.inStock === false || x?.stockQuantity === 0 || x?.stockMax === 0) return 0;
  const limit = Math.max(1, Math.min(10, parseInt(x?.stockMax, 10) || 1));
  return x?.stockQuantity != null ? Math.min(limit, x.stockQuantity) : limit;
}

export const onlyOneInStock = item => item.inStock !== false && item.stockQuantity === 1;
